import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { usePacientes } from '../../hooks/usePacientes';
import { createManipulatedFormula, saveTemplate } from '../../services/manipulatedService';
import { IngredientDatabase } from './IngredientDatabase';
import { FormulaTemplates } from './FormulaTemplates';
import { Ingredient, ManipulatedFormula } from '../../services/mockData';
import { calculateDRIS, DRISValues } from '../../services/drisService';
import { database } from '../../config/backend';
import { ref, get as rtdbGet, child } from '../../services/phpDataAdapter';

interface CreateManipulatedModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  initialPatientId?: string;
  editFormula?: ManipulatedFormula;
}

export const CreateManipulatedModal: React.FC<CreateManipulatedModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialPatientId,
  editFormula,
}) => {
  const { user } = useAuth();
  const { pacientes } = usePacientes();
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'form' | 'templates'>('form');
  const [drisValues, setDrisValues] = useState<DRISValues | null>(null);
  const [loadingPatientData, setLoadingPatientData] = useState(false);

  const [formData, setFormData] = useState({
    patientId: initialPatientId || '',
    formulaName: '',
    ingredients: [] as Ingredient[],
    posology: '',
    duration: 30,
    quantity: 30,
    unit: 'cápsulas',
    laboratory: '',
    validity: 90,
    observations: '',
    saveAsTemplate: false,
  });

  useEffect(() => {
    if (isOpen) {
      if (editFormula) {
        setFormData({
          patientId: editFormula.patientId,
          formulaName: editFormula.formulaName,
          ingredients: editFormula.ingredients,
          posology: editFormula.posology,
          duration: editFormula.duration,
          quantity: editFormula.quantity,
          unit: editFormula.unit,
          laboratory: editFormula.laboratory || '',
          validity: editFormula.validity,
          observations: editFormula.observations || '',
          saveAsTemplate: false,
        });
        loadPatientDRIS(editFormula.patientId);
      } else {
        setFormData({
          patientId: initialPatientId || '',
          formulaName: '',
          ingredients: [],
          posology: '',
          duration: 30,
          quantity: 30,
          unit: 'cápsulas',
          laboratory: '',
          validity: 90,
          observations: '',
          saveAsTemplate: false,
        });
        if (initialPatientId) {
          loadPatientDRIS(initialPatientId);
        } else {
          setDrisValues(null);
        }
      }
    }
  }, [isOpen, editFormula, initialPatientId]);

  const calculateAge = (birthDate: string): number | null => {
    if (!birthDate) return null;
    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const loadPatientDRIS = async (patientId: string) => {
    if (!patientId) {
      setDrisValues(null);
      return;
    }

    setLoadingPatientData(true);
    try {
      const [userSnap, patientSnap] = await Promise.all([
        rtdbGet(child(ref(database), `users/${patientId}`)),
        rtdbGet(child(ref(database), `patients/${patientId}`)),
      ]);

      let age: number | null = null;
      let gender: 'male' | 'female' = 'male';
      let weight: number | undefined = undefined;
      let height: number | undefined = undefined;
      let pregnancyTrimester: 1 | 2 | 3 | undefined = undefined;

      if (userSnap.exists()) {
        const userData = userSnap.val();
        age = userData.personalData?.birthDate
          ? calculateAge(userData.personalData.birthDate)
          : userData.age || null;
        gender = userData.personalData?.gender || userData.gender || 'male';
        weight = userData.weight;
        height = userData.height;
      }

      if (patientSnap.exists()) {
        const patData = patientSnap.val();
        if (!age) age = patData.age || null;
        if (!gender || gender === 'male') gender = patData.gender || gender || 'male';
        if (!weight) weight = patData.weight;
        if (!height) height = patData.height;
      }

      // Se não tiver idade, usar valor padrão de 30 anos
      const finalAge = age || 30;
      const finalGender = gender || 'male';

      const drisData = {
        age: finalAge,
        gender: finalGender,
        weight,
        height,
        pregnancyTrimester,
      };

      const calculatedDRIS = calculateDRIS(drisData);
      setDrisValues(calculatedDRIS);
    } catch (error) {
      console.error('Erro ao carregar dados do paciente para DRIS:', error);
      // Em caso de erro, ainda tenta calcular com valores padrão
      const defaultDRIS = calculateDRIS({
        age: 30,
        gender: 'male',
      });
      setDrisValues(defaultDRIS);
    } finally {
      setLoadingPatientData(false);
    }
  };

  const handlePatientChange = (patientId: string) => {
    setFormData({ ...formData, patientId });
    if (patientId) {
      loadPatientDRIS(patientId);
    } else {
      setDrisValues(null);
    }
  };

  const handleAddIngredient = (ingredient: Ingredient) => {
    setFormData({
      ...formData,
      ingredients: [...formData.ingredients, { ...ingredient }],
    });
  };

  const handleRemoveIngredient = (index: number) => {
    setFormData({
      ...formData,
      ingredients: formData.ingredients.filter((_, i) => i !== index),
    });
  };

  const handleUpdateIngredient = (index: number, updates: Partial<Ingredient>) => {
    const updated = [...formData.ingredients];
    updated[index] = { ...updated[index], ...updates };
    setFormData({ ...formData, ingredients: updated });
  };

  const handleSelectTemplate = (template: any) => {
    setFormData({
      ...formData,
      formulaName: template.formulaName,
      ingredients: template.ingredients.map((i: Ingredient) => ({ ...i })),
      posology: template.posology,
      duration: template.duration,
      quantity: template.quantity,
      unit: template.unit,
      observations: template.observations || '',
    });
    setActiveTab('form');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      alert('Usuário não autenticado');
      return;
    }

    if (!formData.patientId) {
      alert('Selecione um paciente');
      return;
    }

    if (!formData.formulaName.trim()) {
      alert('Preencha o nome da fórmula');
      return;
    }

    if (formData.ingredients.length === 0) {
      alert('Adicione pelo menos um ingrediente');
      return;
    }

    if (!formData.posology.trim()) {
      alert('Preencha a posologia');
      return;
    }

    if (!user?.uid) {
      alert('Usuário não autenticado');
      return;
    }

    setSaving(true);
    try {
      if (editFormula) {
        // TODO: Implementar atualização
        alert('Atualização ainda não implementada');
      } else {
        const formulaData: any = {
          formulaName: formData.formulaName.trim(),
          ingredients: formData.ingredients,
          posology: formData.posology.trim(),
          duration: formData.duration,
          quantity: formData.quantity,
          unit: formData.unit,
          validity: formData.validity,
          status: 'active',
        };

        // Adiciona campos opcionais apenas se tiverem valor
        if (formData.laboratory && formData.laboratory.trim()) {
          formulaData.laboratory = formData.laboratory.trim();
        }
        if (formData.observations && formData.observations.trim()) {
          formulaData.observations = formData.observations.trim();
        }

        await createManipulatedFormula(user.uid, formData.patientId, formulaData);

        if (formData.saveAsTemplate) {
          await saveTemplate(user.uid, {
            formulaName: formData.formulaName,
            ingredients: formData.ingredients,
            posology: formData.posology,
            duration: formData.duration,
            quantity: formData.quantity,
            unit: formData.unit,
            observations: formData.observations || undefined,
          });
        }

        if (onSuccess) {
          onSuccess();
        }
        onClose();
      }
    } catch (error: any) {
      console.error('Erro ao salvar fórmula:', error);
      const errorMessage = error?.message || 'Erro desconhecido ao salvar prescrição';
      alert(`Erro ao salvar prescrição: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={editFormula ? 'Editar Prescrição' : 'Nova Prescrição de Fórmula Manipulada'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? 'Salvando...' : editFormula ? 'Atualizar' : 'Salvar Prescrição'}
          </Button>
        </>
      }
    >
      <div className="ns-tab-row">
        <button
          type="button"
          onClick={() => setActiveTab('form')}
          className={activeTab === 'form' ? 'ns-tab-btn is-active' : 'ns-tab-btn'}
        >
          Formulário
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('templates')}
          className={activeTab === 'templates' ? 'ns-tab-btn is-active' : 'ns-tab-btn'}
        >
          Templates
        </button>
      </div>

      {activeTab === 'templates' ? (
        <FormulaTemplates onSelectTemplate={handleSelectTemplate} />
      ) : (
        <form onSubmit={handleSubmit} className="ns-consultation-form">
          <label className="ns-field">
            <span>Paciente *</span>
            <select
              className="ns-input"
              value={formData.patientId}
              onChange={(e) => handlePatientChange(e.target.value)}
              disabled={saving || !!editFormula}
              required
            >
              <option value="">Selecione um paciente</option>
              {pacientes.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nome}
                </option>
              ))}
            </select>
          </label>

          {loadingPatientData && (
            <div className="ns-loading-text">
              Carregando valores de referência DRIS...
            </div>
          )}

          {drisValues && !loadingPatientData && (
            <div className="ns-dris-box">
              <div className="ns-dris-title">
                <span>📊</span>
                <span>Valores de Referência DRIS (Dietary Reference Intakes)</span>
              </div>

              {/* Macronutrientes */}
              <div style={{ marginBottom: '16px' }}>
                <div className="ns-dris-subtitle">
                  Macronutrientes
                </div>
                <div className="ns-dris-grid">
                  <div className="ns-dris-card">
                    <div className="ns-dris-label">Proteína</div>
                    <div className="ns-dris-value">
                      {drisValues.macronutrients.protein.value} {drisValues.macronutrients.protein.unit}
                    </div>
                  </div>
                  <div className="ns-dris-card">
                    <div className="ns-dris-label">Carboidratos</div>
                    <div className="ns-dris-value">
                      {drisValues.macronutrients.carbohydrates.value} {drisValues.macronutrients.carbohydrates.unit}
                    </div>
                  </div>
                  <div className="ns-dris-card">
                    <div className="ns-dris-label">Gorduras</div>
                    <div className="ns-dris-value">
                      {drisValues.macronutrients.fats.value} {drisValues.macronutrients.fats.unit}
                    </div>
                  </div>
                </div>
              </div>

              {/* Vitaminas */}
              <div style={{ marginBottom: '16px' }}>
                <div className="ns-dris-subtitle">
                  Vitaminas
                </div>
                <div className="ns-dris-grid-auto">
                  {Object.entries(drisValues.vitamins).map(([key, vitamin]) => (
                    <div key={key} className="ns-dris-card">
                      <div className="ns-dris-label">
                        {vitamin.description}
                      </div>
                      <div className="ns-dris-value">
                        {vitamin.value} {vitamin.unit}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Minerais */}
              <div>
                <div className="ns-dris-subtitle">
                  Minerais
                </div>
                <div className="ns-dris-grid-auto">
                  {Object.entries(drisValues.minerals).map(([key, mineral]) => (
                    <div key={key} className="ns-dris-card">
                      <div className="ns-dris-label">
                        {mineral.description}
                      </div>
                      <div className="ns-dris-value">
                        {mineral.value} {mineral.unit}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="ns-dris-note">
                * Valores baseados em RDA (Recommended Dietary Allowance) do IOM, calculados conforme idade e gênero do paciente
              </div>
            </div>
          )}

          <label className="ns-field">
            <span>Nome da Fórmula *</span>
            <input
              type="text"
              className="ns-input"
              value={formData.formulaName}
              onChange={(e) => setFormData({ ...formData, formulaName: e.target.value })}
              placeholder="Ex: Fórmula para Ganho de Massa Muscular"
              disabled={saving}
              required
            />
          </label>

          <div style={{ marginBottom: '16px' }}>
            <label className="ns-field">
              <span>Ingredientes *</span>
            </label>
            <IngredientDatabase
              onSelectIngredient={handleAddIngredient}
              selectedIngredients={formData.ingredients}
            />
          </div>

          {formData.ingredients.length > 0 && (
            <div className="ns-ing-box">
              <div className="ns-ing-title">Ingredientes Selecionados:</div>
              {formData.ingredients.map((ing, index) => (
                <div
                  key={index}
                  className="ns-ing-card"
                >
                  <div style={{ flex: 1 }}>
                    <div className="ns-ing-name">{ing.name}</div>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                      <input
                        type="number"
                        step="0.1"
                        value={ing.quantity}
                        onChange={(e) => handleUpdateIngredient(index, { quantity: parseFloat(e.target.value) || 0 })}
                        className="ns-ing-input"
                        style={{ width: '80px' }}
                        placeholder="Qtd"
                      />
                      <input
                        type="text"
                        value={ing.unit}
                        onChange={(e) => handleUpdateIngredient(index, { unit: e.target.value })}
                        className="ns-ing-input"
                        style={{ width: '60px' }}
                        placeholder="Un"
                      />
                      {ing.purpose && (
                        <span className="ns-ing-purpose">
                          {ing.purpose}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveIngredient(index)}
                    className="ns-ing-remove"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}

          <label className="ns-field">
            <span>Posologia *</span>
            <textarea
              className="ns-textarea"
              rows={3}
              value={formData.posology}
              onChange={(e) => setFormData({ ...formData, posology: e.target.value })}
              placeholder="Ex: 1 cápsula 2x ao dia, após as refeições principais"
              disabled={saving}
              required
            />
          </label>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
            <label className="ns-field">
              <span>Duração (dias) *</span>
              <input
                type="number"
                className="ns-input"
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) || 0 })}
                min="1"
                disabled={saving}
                required
              />
            </label>
            <label className="ns-field">
              <span>Quantidade *</span>
              <input
                type="number"
                className="ns-input"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 0 })}
                min="1"
                disabled={saving}
                required
              />
            </label>
            <label className="ns-field">
              <span>Unidade *</span>
              <select
                className="ns-input"
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                disabled={saving}
                required
              >
                <option value="cápsulas">Cápsulas</option>
                <option value="comprimidos">Comprimidos</option>
                <option value="ml">ml</option>
                <option value="g">g</option>
                <option value="doses">Doses</option>
              </select>
            </label>
            <label className="ns-field">
              <span>Validade (dias) *</span>
              <input
                type="number"
                className="ns-input"
                value={formData.validity}
                onChange={(e) => setFormData({ ...formData, validity: parseInt(e.target.value) || 0 })}
                min="1"
                disabled={saving}
                required
              />
            </label>
          </div>

          <label className="ns-field">
            <span>Laboratório de Manipulação (Opcional)</span>
            <input
              type="text"
              className="ns-input"
              value={formData.laboratory}
              onChange={(e) => setFormData({ ...formData, laboratory: e.target.value })}
              placeholder="Ex: Farmácia de Manipulação XYZ"
              disabled={saving}
            />
          </label>

          <label className="ns-field">
            <span>Observações</span>
            <textarea
              className="ns-textarea"
              rows={3}
              value={formData.observations}
              onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
              placeholder="Observações adicionais sobre a prescrição..."
              disabled={saving}
            />
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px' }}>
            <input
              type="checkbox"
              checked={formData.saveAsTemplate}
              onChange={(e) => setFormData({ ...formData, saveAsTemplate: e.target.checked })}
              disabled={saving}
            />
            <span className="ns-goal-chip-label ns-muted-text">
              Salvar esta fórmula como template para uso futuro
            </span>
          </label>
        </form>
      )}
    </Modal>
  );
};

